import { useMemo } from "react";

/**
 * Generated avatar: motif from the person's name, colour from their city.
 *
 * Everyone in Cary shares a colour; no two people in Cary share a pattern.
 * Deterministic — same name + city always produces the same avatar, with
 * nothing stored anywhere. 12 motifs x 8 rotations x 3 stroke weights x 14
 * city colours ≈ 4,000 combinations.
 */

// 14 colours — enough that neighbouring cities rarely collide, few enough
// that each stays visually distinct.
const PALETTE = [
  { bg: "#E6F1FB", ink: "#185FA5" }, // blue
  { bg: "#FAECE7", ink: "#993C1D" }, // coral
  { bg: "#E1F5EE", ink: "#0F6E56" }, // teal
  { bg: "#EEEDFE", ink: "#534AB7" }, // purple
  { bg: "#EAF3DE", ink: "#3B6D11" }, // green
  { bg: "#FBEAF0", ink: "#993556" }, // pink
  { bg: "#FAEEDA", ink: "#854F0B" }, // amber
  { bg: "#F1EFE8", ink: "#5F5E5A" }, // stone
  { bg: "#FCEBEB", ink: "#A32D2D" }, // red
  { bg: "#DFF0F5", ink: "#12657C" }, // cyan
  { bg: "#F0EBF8", ink: "#6B3D9E" }, // violet
  { bg: "#F5F0E3", ink: "#7A5C12" }, // olive
  { bg: "#E8F0EC", ink: "#2F6B4F" }, // sage
  { bg: "#F7ECE4", ink: "#8A4B24" }, // clay
];

const NEUTRAL = 7; // stone — reserved for posts with no city

// Hashing alone put Raleigh, Winston-Salem and Holly Springs on the same
// colour. The cities our community actually uses are pinned by hand so the
// ones people see every day are guaranteed distinct; everything else hashes.
const CITY_COLOR = {
  cary: 0,
  raleigh: 1,
  durham: 2,
  charlotte: 3,
  morrisville: 4,
  apex: 5,
  chapelhill: 6,
  hollysprings: 8,
  greensboro: 9,
  wakeforest: 10,
  winstonsalem: 11,
  huntersville: 12,
  wilmington: 13,
  asheville: 2,
  garner: 5,
  clayton: 6,
};

// Each motif draws inside a 40x40 box centred on (20,20).
// `spin` marks the group that rotates; `dot` adds a breathing centre.
const MOTIFS = [
  // 0 — quincunx / lotus
  (s) => <><g className="kv-spin"><circle cx="20" cy="10.5" r="5" strokeWidth={s}/><circle cx="20" cy="29.5" r="5" strokeWidth={s}/><circle cx="10.5" cy="20" r="5" strokeWidth={s}/><circle cx="29.5" cy="20" r="5" strokeWidth={s}/></g></>,
  // 1 — nested diamonds
  (s) => <><g className="kv-spin-r"><path d="M20 7 L33 20 L20 33 L7 20 Z" strokeWidth={s}/><path d="M20 13.5 L26.5 20 L20 26.5 L13.5 20 Z" strokeWidth={s}/></g></>,
  // 2 — vesica petals
  (s) => <><g className="kv-spin"><path d="M20 6 C27.5 13 27.5 27 20 34 C12.5 27 12.5 13 20 6 Z" strokeWidth={s}/><path d="M6 20 C13 12.5 27 12.5 34 20 C27 27.5 13 27.5 6 20 Z" strokeWidth={s}/></g></>,
  // 3 — concentric rings + cardinal dots
  (s) => <><g className="kv-spin-r"><circle cx="20" cy="20" r="12.5" strokeWidth={s}/><circle cx="20" cy="20" r="7.5" strokeWidth={s}/></g><g className="kv-spin kv-fill"><circle cx="20" cy="4.8" r="1.7"/><circle cx="35.2" cy="20" r="1.7"/><circle cx="20" cy="35.2" r="1.7"/><circle cx="4.8" cy="20" r="1.7"/></g></>,
  // 4 — eight-ray star
  (s) => <><g className="kv-spin"><path d="M20 5 L20 35 M5 20 L35 20 M9.4 9.4 L30.6 30.6 M30.6 9.4 L9.4 30.6" strokeWidth={s}/></g></>,
  // 5 — squircle rosette
  (s) => <><g className="kv-spin-r"><path d="M12.5 12.5 Q20 5 27.5 12.5 Q35 20 27.5 27.5 Q20 35 12.5 27.5 Q5 20 12.5 12.5 Z" strokeWidth={s}/><path d="M16 16 Q20 12 24 16 Q28 20 24 24 Q20 28 16 24 Q12 20 16 16 Z" strokeWidth={s}/></g></>,
  // 6 — hexagon nest
  (s) => <><g className="kv-spin"><path d="M20 8 L28.4 13 L28.4 27 L20 32 L11.6 27 L11.6 13 Z" strokeWidth={s}/><path d="M20 14.5 L24.6 17.2 L24.6 22.8 L20 25.5 L15.4 22.8 L15.4 17.2 Z" strokeWidth={s}/></g></>,
  // 7 — bi-petal cross
  (s) => <><g className="kv-spin-r"><path d="M20 5 Q27 20 20 35 Q13 20 20 5 Z" strokeWidth={s}/><path d="M5 20 Q20 13 35 20 Q20 27 5 20 Z" strokeWidth={s}/></g></>,
  // 8 — interlaced squares
  (s) => <><g className="kv-spin"><rect x="10" y="10" width="20" height="20" rx="2" strokeWidth={s}/><rect x="10" y="10" width="20" height="20" rx="2" transform="rotate(45 20 20)" strokeWidth={s}/></g></>,
  // 9 — spiral arcs
  (s) => <><g className="kv-spin-r"><path d="M20 20 Q20 9 29 8 M20 20 Q31 20 32 29 M20 20 Q20 31 11 32 M20 20 Q9 20 8 11" strokeWidth={s}/></g></>,
  // 10 — star of triangles
  (s) => <><g className="kv-spin"><path d="M20 6 L31.5 26 L8.5 26 Z" strokeWidth={s}/><path d="M20 34 L8.5 14 L31.5 14 Z" strokeWidth={s}/></g></>,
  // 11 — four teardrops
  (s) => <><g className="kv-spin-r"><path d="M20 7 Q26 13 20 20 Q14 13 20 7 Z" strokeWidth={s}/><path d="M33 20 Q27 26 20 20 Q27 14 33 20 Z" strokeWidth={s}/><path d="M20 33 Q14 27 20 20 Q26 27 20 33 Z" strokeWidth={s}/><path d="M7 20 Q13 14 20 20 Q13 26 7 20 Z" strokeWidth={s}/></g></>,
];

const CENTRE_DOT = new Set([0, 1, 3, 7, 9]);
const STROKES = [1.3, 1.5, 1.75];

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export function KolamAvatar({ name = "Anonymous", city, size = 36, className = "" }) {
  const { motif, rotation, stroke, colors, dot } = useMemo(() => {
    // Pattern comes from the person
    const h = hash((name || "Anonymous").trim().toLowerCase());
    const idx = h % MOTIFS.length;

    // Colour comes from the city. Normalised so "winston salem",
    // "Winston-Salem" and "WINSTON SALEM" all land on the same colour.
    const cityKey = (city || "").toLowerCase().replace(/[^a-z]/g, "");
    let colorIdx = NEUTRAL;
    if (cityKey) {
      colorIdx = CITY_COLOR[cityKey] ?? hash(cityKey) % PALETTE.length;
    }
    const colors = PALETTE[colorIdx];

    return {
      motif: idx,
      rotation: ((h >> 4) % 8) * 45,
      stroke: STROKES[(h >> 8) % STROKES.length],
      colors,
      dot: CENTRE_DOT.has(idx),
    };
  }, [name, city]);

  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      className={`kolam-avatar shrink-0 ${className}`}
      style={{ borderRadius: "50%" }}
      role="img"
      aria-label={`${name}${city ? ` from ${city}` : ""}`}
    >
      <circle cx="20" cy="20" r="20" fill={colors.bg} />
      <g
        transform={`rotate(${rotation} 20 20)`}
        stroke={colors.ink}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {MOTIFS[motif](stroke)}
      </g>
      {dot && <circle className="kv-pulse" cx="20" cy="20" r="2.3" fill={colors.ink} />}
    </svg>
  );
}
